(() => {
  'use strict';

  const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const CHAT_COLLISION_TARGETS = [
    '[data-rv-add-to-cart]',
    '[data-rv-card-quick-buy]',
    '[data-rv-facets-open]',
    '[data-rv-cart-checkout]',
    '.rv-cart__actions [name="checkout"]',
    '.rv-cart__summary',
    '.rv-info-detail__nav',
    '.rv-info-preview__nav'
  ].join(',');

  const CHAT_MOBILE_VIEWPORT = window.matchMedia('(max-width: 46.874rem)');
  const CHAT_ACTIVATOR_SIZE = 46;
  const CHAT_VIEWPORT_EDGE = 20;
  const CHAT_COLLISION_GAP = 8;

  function setPageMenuState(isOpen) {
    document.body.classList.toggle('rv-menu-open', isOpen);
  }

  function initialiseMenuDialog(dialog) {
    if (dialog.dataset.rvReady === 'true') return;

    const trigger = document.querySelector(`[data-rv-menu-open][aria-controls="${dialog.id}"]`);
    const closeButton = dialog.querySelector('[data-rv-menu-close]');
    const wideViewport = window.matchMedia('(min-width: 61.875rem)');
    let usedNativeModal = false;
    let restoreFocusOnClose = true;

    if (!trigger || !closeButton) return;

    const focusFirstControl = () => {
      window.requestAnimationFrame(() => closeButton.focus());
    };

    const openDialog = () => {
      if (dialog.open) return;

      usedNativeModal = typeof dialog.showModal === 'function';
      if (usedNativeModal) {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
        dialog.setAttribute('role', 'dialog');
        dialog.setAttribute('aria-modal', 'true');
      }

      trigger.setAttribute('aria-expanded', 'true');
      setPageMenuState(true);
      focusFirstControl();
    };

    const restoreTrigger = () => {
      trigger.setAttribute('aria-expanded', 'false');
      setPageMenuState(false);
      if (restoreFocusOnClose && document.contains(trigger)) trigger.focus();
      restoreFocusOnClose = true;
    };

    const closeDialog = (restoreFocus = true) => {
      if (!dialog.open) return;
      restoreFocusOnClose = restoreFocus;

      if (typeof dialog.close === 'function') {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
        if (restoreFocus) restoreTrigger();
      }

      if (!restoreFocus) {
        trigger.setAttribute('aria-expanded', 'false');
        setPageMenuState(false);
      }
    };

    trigger.addEventListener('click', openDialog);
    closeButton.addEventListener('click', () => closeDialog());

    dialog.addEventListener('close', restoreTrigger);
    dialog.addEventListener('cancel', () => {
      trigger.setAttribute('aria-expanded', 'false');
      setPageMenuState(false);
    });

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });

    dialog.addEventListener('click', (event) => {
      if (event.target.closest('a[href]')) closeDialog(false);
    });

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !usedNativeModal) {
        event.preventDefault();
        closeDialog();
        return;
      }

      if (event.key !== 'Tab' || usedNativeModal) return;

      const focusable = [...dialog.querySelectorAll(FOCUSABLE)].filter(
        (element) => !element.hasAttribute('hidden') && element.offsetParent !== null
      );
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    const handleViewportChange = (event) => {
      if (event.matches && dialog.open) closeDialog(false);
    };

    if (typeof wideViewport.addEventListener === 'function') {
      wideViewport.addEventListener('change', handleViewportChange);
    } else {
      wideViewport.addListener(handleViewportChange);
    }

    dialog.dataset.rvReady = 'true';
  }

  function initialiseDisclosures(scope = document) {
    scope.querySelectorAll('[data-rv-disclosure]').forEach((details) => {
      if (details.dataset.rvReady === 'true') return;

      details.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !details.open) return;
        details.open = false;
        details.querySelector('summary')?.focus();
      });

      details.dataset.rvReady = 'true';
    });
  }

  function initialiseHeader(scope = document) {
    scope.querySelectorAll('[data-rv-header]').forEach((header) => {
      if (header.dataset.rvReady === 'true') return;

      let ticking = false;
      const update = () => {
        header.classList.toggle('is-scrolled', window.scrollY > 12);
        ticking = false;
      };
      const requestUpdate = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      };

      update();
      window.addEventListener('scroll', requestUpdate, { passive: true });
      header.dataset.rvReady = 'true';
    });
  }

  function initialiseChatCollisionGuard() {
    if (document.documentElement.dataset.rvChatCollisionReady === 'true') return;

    let animationFrame = 0;
    let cleanedUp = false;
    let intersectionReady = false;
    const trackedTargets = new Set();
    const intersectingTargets = new Set();

    const overlapsReservedCorner = (element) => {
      if (element.closest('[hidden], [aria-hidden="true"]')) return false;
      const style = window.getComputedStyle(element);
      if (style.display === 'none' || style.visibility === 'hidden') return false;

      const viewport = window.visualViewport;
      const viewportLeft = viewport?.offsetLeft || 0;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportRight = viewportLeft + (viewport?.width || window.innerWidth);
      const viewportBottom = viewportTop + (viewport?.height || window.innerHeight);
      const reservedSize = CHAT_VIEWPORT_EDGE + CHAT_ACTIVATOR_SIZE + CHAT_COLLISION_GAP;
      const rect = element.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) return false;

      return rect.right > viewportRight - reservedSize
        && rect.left < viewportRight
        && rect.bottom > viewportBottom - reservedSize
        && rect.top < viewportBottom;
    };

    const update = () => {
      animationFrame = 0;
      if (cleanedUp) return;
      const chatHosts = [...document.querySelectorAll('shopify-chat')];
      if (!chatHosts.length) return;

      const collisionCandidates = intersectionReady ? intersectingTargets : trackedTargets;
      const hasCollision = CHAT_MOBILE_VIEWPORT.matches
        && [...collisionCandidates].some(overlapsReservedCorner);

      chatHosts.forEach((chatHost) => {
        const launcherHasFocus = hasCollision
          && (document.activeElement === chatHost || chatHost.matches(':focus-within'));
        const nextState = hasCollision ? (launcherHasFocus ? 'focused' : 'hidden') : null;
        const currentState = chatHost.getAttribute('data-rv-chat-collision');
        if (currentState === nextState) return;
        if (nextState) chatHost.setAttribute('data-rv-chat-collision', nextState);
        else chatHost.removeAttribute('data-rv-chat-collision');
      });
    };

    const requestUpdate = () => {
      if (animationFrame) return;
      animationFrame = window.requestAnimationFrame(update);
    };

    const intersectionObserver = typeof IntersectionObserver === 'function'
      ? new IntersectionObserver((entries) => {
        intersectionReady = true;
        entries.forEach((entry) => {
          if (entry.isIntersecting) intersectingTargets.add(entry.target);
          else intersectingTargets.delete(entry.target);
        });
        requestUpdate();
      })
      : null;

    const syncTargets = () => {
      if (cleanedUp) return;
      const currentTargets = new Set(document.querySelectorAll(CHAT_COLLISION_TARGETS));

      trackedTargets.forEach((target) => {
        if (currentTargets.has(target)) return;
        trackedTargets.delete(target);
        intersectingTargets.delete(target);
        intersectionObserver?.unobserve(target);
      });

      currentTargets.forEach((target) => {
        if (trackedTargets.has(target)) return;
        trackedTargets.add(target);
        intersectionObserver?.observe(target);
      });

      requestUpdate();
    };

    const mutationObserver = new MutationObserver(syncTargets);
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate, { passive: true });
    window.addEventListener('orientationchange', requestUpdate, { passive: true });
    window.visualViewport?.addEventListener('scroll', requestUpdate, { passive: true });
    window.visualViewport?.addEventListener('resize', requestUpdate, { passive: true });
    document.addEventListener('focusin', requestUpdate, true);
    document.addEventListener('focusout', requestUpdate, true);
    document.addEventListener('shopify:section:load', syncTargets);
    document.addEventListener('shopify:section:unload', syncTargets);

    if (typeof CHAT_MOBILE_VIEWPORT.addEventListener === 'function') {
      CHAT_MOBILE_VIEWPORT.addEventListener('change', requestUpdate);
    } else {
      CHAT_MOBILE_VIEWPORT.addListener(requestUpdate);
    }

    const cleanup = () => {
      if (cleanedUp) return;
      cleanedUp = true;
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      intersectionObserver?.disconnect();
      mutationObserver.disconnect();
      trackedTargets.clear();
      intersectingTargets.clear();

      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      window.removeEventListener('orientationchange', requestUpdate);
      window.visualViewport?.removeEventListener('scroll', requestUpdate);
      window.visualViewport?.removeEventListener('resize', requestUpdate);
      document.removeEventListener('focusin', requestUpdate, true);
      document.removeEventListener('focusout', requestUpdate, true);
      document.removeEventListener('shopify:section:load', syncTargets);
      document.removeEventListener('shopify:section:unload', syncTargets);

      if (typeof CHAT_MOBILE_VIEWPORT.removeEventListener === 'function') {
        CHAT_MOBILE_VIEWPORT.removeEventListener('change', requestUpdate);
      } else {
        CHAT_MOBILE_VIEWPORT.removeListener(requestUpdate);
      }

      document.querySelectorAll('shopify-chat[data-rv-chat-collision]').forEach((chatHost) => {
        chatHost.removeAttribute('data-rv-chat-collision');
      });
      delete document.documentElement.dataset.rvChatCollisionReady;
    };

    window.addEventListener('pagehide', cleanup, { once: true });
    document.documentElement.dataset.rvChatCollisionReady = 'true';
    syncTargets();
  }


  function initialise(scope = document) {
    scope.querySelectorAll('[data-rv-menu-dialog]').forEach(initialiseMenuDialog);
    initialiseDisclosures(scope);
    initialiseHeader(scope);
    initialiseChatCollisionGuard();
  }

  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-rv-disclosure][open]').forEach((details) => {
      if (!details.contains(event.target)) details.open = false;
    });
  });

  initialise();

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
